import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { PDFDocument } from 'pdf-lib';
import { axiosInstance, API } from '@/App';
import { useTranslation } from 'react-i18next';

// Configure PDF.js worker - use CDN for reliable loading
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const WorkerFillForm = ({ user }) => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [pdfError, setPdfError] = useState(null);
  const [pageSize, setPageSize] = useState({ width: 612, height: 792 });
  const [pdfRetryCount, setPdfRetryCount] = useState(0);
  const [pdfKey, setPdfKey] = useState(0);
  
  const containerRef = useRef(null);

  useEffect(() => {
    fetchForm();
  }, [formId]);

  // Auto-scale PDF to fit container - handles mobile and desktop
  const calculateScale = () => {
    if (containerRef.current && form) {
      const containerWidth = containerRef.current.clientWidth - 20; // padding
      const pdfWidth = form.fields?.[0]?.pageWidth || 612;
      // On mobile (< 768px), scale down more aggressively
      const isMobile = window.innerWidth < 768;
      const maxScale = isMobile ? 1.0 : 1.5;
      const minScale = isMobile ? 0.4 : 0.5;
      const newScale = Math.max(minScale, Math.min(containerWidth / pdfWidth, maxScale));
      setScale(newScale);
    }
  };

  useEffect(() => {
    calculateScale();
    // Add resize listener for responsive behavior
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, [form]);

  const fetchForm = async () => {
    try {
      const response = await axiosInstance.get(`/worker/forms/${formId}`);
      setForm(response.data);
      
      // Initialize form data
      const initialData = {};
      response.data.fields?.forEach(field => {
        initialData[field.fieldId] = field.fieldType === 'checkbox' ? false : '';
      });
      setFormData(initialData);
      
      // Set page size from first field
      if (response.data.fields?.[0]) {
        setPageSize({
          width: response.data.fields[0].pageWidth || 612,
          height: response.data.fields[0].pageHeight || 792
        });
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching form:', error);
      alert('Error loading form');
      setLoading(false);
    }
  };

  // Track actual rendered PDF size
  const [renderedPdfSize, setRenderedPdfSize] = useState({ width: 612, height: 792 });

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfError(null);
  };

  const onPageLoadSuccess = (page) => {
    // Get the actual rendered size at scale=1
    const { width, height } = page;
    console.log(`WorkerFillForm: PDF page rendered at: ${width} x ${height} (unscaled)`);
    setRenderedPdfSize({ width, height });
  };

  const onDocumentLoadError = (error) => {
    console.error('PDF Load Error:', error, 'Retry count:', pdfRetryCount);
    
    // Auto-retry up to 3 times
    if (pdfRetryCount < 3) {
      const delay = (pdfRetryCount + 1) * 1000;
      console.log(`Retrying PDF load in ${delay}ms...`);
      setTimeout(() => {
        setPdfRetryCount(prev => prev + 1);
        setPdfKey(prev => prev + 1);
        setPdfError(null);
      }, delay);
    } else {
      setPdfError('Could not load PDF preview after multiple attempts');
    }
  };

  const handleInputChange = (fieldId, value) => {
    setFormData({ ...formData, [fieldId]: value });
    if (errors[fieldId]) {
      setErrors({ ...errors, [fieldId]: null });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const newErrors = {};
    form.fields?.forEach(field => {
      if (field.required) {
        const value = formData[field.fieldId];
        if (field.fieldType === 'checkbox') {
          if (value !== true) {
            newErrors[field.fieldId] = 'Required';
          }
        } else if (!value || value === '') {
          newErrors[field.fieldId] = 'Required';
        }
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      alert('Please fill in all required fields (marked with red borders)');
      return;
    }

    setSubmitting(true);

    try {
      const response = await axiosInstance.post(
        `/worker/forms/${formId}/submit`,
        formData
      );

      alert('Form submitted successfully!');
      navigate('/worker/onboarding', { replace: true });
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Error submitting form: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  // Use the API endpoint for PDF (has GridFS fallback if file missing from disk)
  const token = localStorage.getItem('token');
  const pdfUrl = form?.id && token
    ? `${API}/custom-forms/${form.id}/pdf?token=${token}`
    : null;

  // Get fields for current page
  const pageFields = form?.fields?.filter(f => f.page === currentPage) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-xl text-gray-600">Loading form...</div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-xl text-red-600">Form not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b px-2 sm:px-4 py-3 sticky top-0 z-20 shadow-sm">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">{form.formName}</h1>
            <p className="text-xs sm:text-sm text-gray-500">Fill in the fields on the form below</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={() => navigate('/worker/onboarding', { replace: true })}
              className="px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-xs sm:text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-3 sm:px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 text-xs sm:text-sm"
            >
              {submitting ? t('forms.submittingForm') : t('common.submit')}
            </button>
          </div>
        </div>
      </div>

      {/* Page Navigation */}
      {numPages > 1 && (
        <div className="bg-white border-b px-4 py-2 sticky top-14 z-10">
          <div className="max-w-4xl mx-auto flex justify-center items-center gap-4">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded disabled:opacity-50 text-sm"
            >
              ← Previous
            </button>
            <span className="text-sm text-gray-600">
              {t('forms.page')} {currentPage} {t('forms.of')} {numPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(numPages, currentPage + 1))}
              disabled={currentPage === numPages}
              className="px-3 py-1 border rounded disabled:opacity-50 text-sm"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* PDF with Overlay Fields */}
      <div className="w-full max-w-4xl mx-auto p-2 sm:p-4">
        <div 
          ref={containerRef}
          className="bg-white rounded-lg shadow-lg overflow-x-auto"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {pdfError || !pdfUrl ? (
            <div className="p-8 text-center text-red-600">
              <p>{pdfError || 'PDF not available'}</p>
              <button
                onClick={() => {
                  setPdfRetryCount(0);
                  setPdfKey(prev => prev + 1);
                  setPdfError(null);
                }}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 mr-4"
              >
                Retry Loading PDF
              </button>
              {pdfUrl && (
                <a 
                  href={pdfUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="mt-4 inline-block text-blue-600 hover:underline"
                >
                  Open PDF in new tab
                </a>
              )}
            </div>
          ) : (
            <div className="relative inline-block">
              <Document
                key={pdfKey}
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <div className="flex items-center justify-center h-96">
                    <div className="text-gray-500">Loading PDF...{pdfRetryCount > 0 ? ` (Attempt ${pdfRetryCount + 1})` : ''}</div>
                  </div>
                }
              >
                <Page 
                  pageNumber={currentPage} 
                  scale={scale}
                  onLoadSuccess={onPageLoadSuccess}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>

              {/* Overlay Input Fields */}
              {pageFields.map((field) => {
                const hasError = errors[field.fieldId];
                // Use coordinates directly with scale (same as AdminFormMapper)
                const scaledX = field.x * scale;
                const scaledY = field.y * scale;
                const scaledWidth = field.width * scale;
                const scaledHeight = field.height * scale;

                return (
                  <div
                    key={field.fieldId}
                    className="absolute"
                    style={{
                      left: `${scaledX}px`,
                      top: `${scaledY}px`,
                      width: `${scaledWidth}px`,
                      height: `${scaledHeight}px`,
                    }}
                  >
                    {field.fieldType === 'checkbox' ? (
                      <input
                        type="checkbox"
                        checked={formData[field.fieldId] || false}
                        onChange={(e) => handleInputChange(field.fieldId, e.target.checked)}
                        className={`w-full h-full cursor-pointer ${hasError ? 'ring-2 ring-red-500' : ''}`}
                        title={field.label}
                      />
                    ) : (
                      <input
                        type={field.fieldType === 'date' ? 'date' : field.fieldType === 'ssn' ? 'password' : 'text'}
                        value={formData[field.fieldId] || ''}
                        onChange={(e) => handleInputChange(field.fieldId, e.target.value)}
                        placeholder={field.placeholder || ''}
                        className={`w-full h-full px-1 text-xs bg-yellow-50 bg-opacity-80 border ${
                          hasError ? 'border-red-500 bg-red-50' : 'border-blue-300'
                        } focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white`}
                        style={{ fontSize: `${Math.max(10, scaledHeight * 0.7)}px` }}
                        title={field.label}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Field Legend */}
        <div className="mt-4 p-4 bg-white rounded-lg shadow">
          <h3 className="font-semibold text-gray-900 mb-2">Instructions</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• <span className="bg-yellow-50 border border-blue-300 px-2 py-0.5 text-xs">Yellow fields</span> - Click to type</li>
            <li>• <span className="bg-red-50 border border-red-500 px-2 py-0.5 text-xs">Red border</span> - Required field (must fill)</li>
            <li>• Use the page navigation above if the form has multiple pages</li>
            <li>• Click &quot;Submit Form&quot; when finished</li>
          </ul>
        </div>

        {/* Mobile Submit Button */}
        <div className="mt-4 pb-8">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full px-6 py-4 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 font-medium text-lg"
          >
            {submitting ? 'Submitting...' : 'Submit Form'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkerFillForm;
